import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthShell from '../main/AuthShell';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import { clearCart } from '../shop/cart';
import './Storefront.css';

/* "Confirm my order" (/:slug/confirm-order) — the target of the mail a buyer gets
   when they ask to be invoiced instead of paying by card.

   Their order and its proposal already exist; this page is what promotes that
   proposal into a numbered tax invoice and sends it. Opening the link is the proof
   that the address on the order is theirs — the reason the promotion waited for it
   at all. On success it hands over to the normal confirmation page.

   ★ The token arrives as `sf_confirm`, not `token`: the App-global
   usePersistUrlParams hook strips every query param on arrival and stashes it in
   sessionStorage, so a page-level param needs a name of its own (same reason as
   sf_mode/sf_rate) and has to be read from both places. */

const StorefrontConfirmOrder = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const token = query.get('sf_confirm') || sessionStorage.getItem('sf_confirm') || '';

  const [phase, setPhase] = useState(token ? 'working' : 'invalid'); // working|invalid|error
  const [error, setError] = useState(null);
  // React 18 StrictMode mounts effects twice in development. Issuing an invoice is
  // not something to do twice — the server answers an already-final document with
  // the same receipt, but the guard keeps the screen honest either way.
  const sent = useRef(false);

  useEffect(() => {
    if (!token || sent.current) return;
    sent.current = true;
    (async () => {
      try {
        const data = await apiRequest('POST', '/storefront/confirm-order', { token });
        sessionStorage.removeItem('sf_confirm');
        // The server signed them in on the strength of this link, so the app has to
        // agree: without this the receipt's buttons lead to a role picker that finds
        // no roles, which is exactly what it looked like before.
        if (data.confirmed) {
          localStorage.setItem('isLoggedIn', '1');
          window.dispatchEvent(new Event('auth-changed'));
        }
        if (data.event_id || data.order_id) clearCart(data.event_id);
        navigate(`/${slug}/confirmation`, { replace: true, state: { result: data, slug } });
      } catch (err) {
        // An expired link, an order that was cancelled in the back office, a
        // billing entity with no number series — the buyer can act on none of it,
        // so they get one message and a way back to the shop.
        setError(err.message);
        setPhase(err.message === 'confirmLinkInvalid' ? 'invalid' : 'error');
      }
    })();
  }, [token, slug, navigate]);

  return (
    <AuthShell title={t('confirmYourOrderTitle')}>
      <div className="auth__card">
        {phase === 'working' ? (
          <>
            <span className="auth__spinner" />
            <p className="auth__hint">{t('confirmOrderWorking')}</p>
          </>
        ) : (
          <>
            <b>{t(phase === 'invalid' ? 'confirmLinkInvalid' : 'confirmOrderFailed')}</b>
            {phase === 'error' && error && (
              <p className="auth__hint">{t(error, { defaultValue: error })}</p>
            )}
            <p className="auth__hint">{t('confirmOrderFailedHelp')}</p>
            <Link to={`/${slug}`} className="auth__primary">
              <Icon name="ticket" />
              {t('backToShop', { defaultValue: 'Back' })}
            </Link>
          </>
        )}
      </div>
    </AuthShell>
  );
};

export default StorefrontConfirmOrder;
